import {
  useEffect,
  useRef,
} from 'react';

import { InputWrapper } from '@/core/components/InputWrapper';

import { fieldDescribedBy } from '@/utils/fieldIds';

type AutocompleteValue =
  | 'additional-name'
  | 'address-level1'
  | 'address-level2'
  | 'address-level3'
  | 'address-level4'
  | 'address-line1'
  | 'address-line2'
  | 'address-line3'
  | 'bday'
  | 'bday-day'
  | 'bday-month'
  | 'bday-year'
  | 'billing'
  | 'cc-additional-name'
  | 'cc-csc'
  | 'cc-exp'
  | 'cc-exp-month'
  | 'cc-exp-year'
  | 'cc-family-name'
  | 'cc-given-name'
  | 'cc-name'
  | 'cc-number'
  | 'cc-type'
  | 'country'
  | 'country-name'
  | 'current-password'
  | 'email'
  | 'family-name'
  | 'fax'
  | 'given-name'
  | 'home'
  | 'honorific-prefix'
  | 'honorific-suffix'
  | 'impp'
  | 'language'
  | 'mobile'
  | 'name'
  | 'new-password'
  | 'nickname'
  | 'off'
  | 'on'
  | 'one-time-code'
  | 'organization'
  | 'organization-title'
  | 'pager'
  | 'photo'
  | 'postal-code'
  | 'sex'
  | 'shipping'
  | 'street-address'
  | 'tel'
  | 'tel-area-code'
  | 'tel-country-code'
  | 'tel-extension'
  | 'tel-local'
  | 'tel-national'
  | 'transaction-amount'
  | 'transaction-currency'
  | 'url'
  | 'username'
  | 'work';

interface FormInputProps {
  autocomplete?: AutocompleteValue | null;
  backgroundcolor: string;
  error: () => string | false;
  /**
   * Optional description of why the field exists or how it is used.
   * Always exposed to screen readers via aria-describedby; visually
   * revealed on demand through a small help toggle inside the field.
   */
  helpText?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onBlur?: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Optional callback invoked when the browser fires the `invalid` event on the input.
   * Called after the native tooltip is suppressed via `event.preventDefault()`.
   * Use this to set a styled inline error message in place of the browser's native tooltip.
   */
  onInvalidField?: () => void;
  name: string;
  label?: string;
  type?: string;
  value: string | number | undefined;
  maxlength?: number;
  ariaLabel?: string;
}

export const FormInput = ({
  autocomplete,
  backgroundcolor,
  error,
  helpText,
  isDisabled,
  isRequired,
  maxlength,
  name,
  onBlur,
  onChange,
  onFocus,
  onInvalidField,
  label,
  type,
  value,
  ariaLabel,
}: FormInputProps) => {
  const fieldName = label || name;
  const inputRef = useRef<HTMLInputElement>(null);

  const describedBy = fieldDescribedBy({
    fieldName: name,
    hasError: !!error(),
    hasHelp: !!helpText,
  });

  // Clear native validity state when a value is provided programmatically
  // (e.g. zip code set by address autocomplete), so the browser does not
  // block the next submission attempt.
  useEffect(() => {
    const input = inputRef.current;

    if (input && value) {
      input.setCustomValidity('');
    }
  }, [value]);

  const handleInvalid = (event: React.InvalidEvent<HTMLInputElement>) => {
    if (onInvalidField) {
      event.preventDefault();
      onInvalidField();
    }

    const input = event.target;

    if (input.validity.valueMissing) {
      input.setCustomValidity(`Please fill out the ${fieldName.toLowerCase()} field`);
    } else {
      input.setCustomValidity('');
    }
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Clear custom validity message when user starts typing
    event.target.setCustomValidity('');
    onChange(event);
  };

  return (
    <InputWrapper
      backgroundcolor={backgroundcolor}
      error={error}
      fieldName={name}
      helpText={helpText}
      isRequired={isRequired}
    >
      <input
        aria-describedby={describedBy}
        aria-invalid={!!error()}
        aria-label={ariaLabel || label || name}
        autoComplete={autocomplete ?? undefined}
        disabled={isDisabled}
        type={type}
        name={name}
        placeholder=" "
        onBlur={() => {
          if (onBlur) {
            onBlur();
          }
        }}
        onChange={handleInput}
        onFocus={onFocus}
        onInvalid={handleInvalid}
        ref={inputRef}
        required={isRequired}
        value={value}
        maxLength={maxlength}
      />
      <span>{label}</span>
    </InputWrapper>
  );
};

FormInput.defaultProps = {
  helpText: '',
  isDisabled: false,
  isRequired: false,
  label: '',
  maxlength: null,
  onBlur: () => null,
  onFocus: () => null,
  type: 'text',
};
